import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import Sidebar from "../components/Sidebar";
import TopNavbar from "../components/TopNavbar";
import { Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Stack, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { People, CalendarToday, EventAvailable } from '@mui/icons-material';

const formatDate = (dateString) => {
    return dateString ? format(new Date(dateString), 'dd.MM.yyyy') : 'N/A';
};

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        todayVisits: 0,
        monthlyVisits: 0,
        totalPatients: 0
    });
    const [recentVisits, setRecentVisits] = useState([]);
    const [visitsPage, setVisitsPage] = useState(1);
    const [visitsPerPage, setVisitsPerPage] = useState(10);
    const [visitsTotalPages, setVisitsTotalPages] = useState(1);
    const [visitsTotal, setVisitsTotal] = useState(0);
    const [visitsLoading, setVisitsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError("User is not authenticated.");
                return;
            }

            try {
                const response = await axios.get('https://horizontclinic.com/horizont-be/getDashboardData.php', {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true
                });

                if (response.status === 200 && response.data) {
                    setDashboardData({
                        todayVisits: response.data.todayVisits || 0,
                        monthlyVisits: response.data.monthlyVisits || 0,
                        totalPatients: response.data.totalPatients || 0
                    });
                } else {
                    setError("Failed to load dashboard data");
                }
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
                setError("Failed to load dashboard data");
            }
        };

        const fetchRecentVisits = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;
            setVisitsLoading(true);
            try {
                const response = await axios.get('https://horizontclinic.com/horizont-be/getRecentVisits.php', {
                    params: { page: visitsPage, per_page: visitsPerPage },
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data?.status === 'success') {
                    setRecentVisits(response.data.data || []);
                    setVisitsTotalPages(response.data.meta?.total_pages || 1);
                    setVisitsTotal(response.data.meta?.total || 0);
                } else {
                    setError('Failed to load recent visits');
                }
            } catch (error) {
                if (error?.response?.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                console.error('Error fetching recent visits:', error);
                setError('Failed to load recent visits');
            } finally {
                setVisitsLoading(false);
            }
        };

        fetchDashboardData();
        fetchRecentVisits();
    }, [visitsPage, visitsPerPage]);

    const cardStyles = {
        padding: '20px',
        color: '#fff'
    };

    const iconStyles = {
        fontSize: '3rem',
        marginRight: '20px'
    };

    return (
        <div className="d-flex">
            <Sidebar />
            <div className="flex-grow-1">
                <TopNavbar />
                <div className="p-4">
                    <h1 className={'mb-4'}>Dashboard</h1>
                    <Grid container spacing={3}>
                        {/* Visits Today Card */}
                        <Grid item xs={12} md={4}>
                            <Card className={"shadow bg-primary"}>
                                <CardContent style={cardStyles}>
                                    <div className="row">
                                        <div className="col-4 d-flex align-items-center">
                                            <EventAvailable style={{ ...iconStyles, color: '#fff' }} />
                                        </div>
                                        <div className="col-8 text-center">
                                            <Typography variant="h6" gutterBottom>
                                                Visits Today
                                            </Typography>
                                            <Typography variant="h3" style={{ fontWeight: 700 }}>
                                                {dashboardData.todayVisits}
                                            </Typography>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Visits This Month Card */}
                        <Grid item xs={12} md={4}>
                            <Card style={{ backgroundColor: '#2196f3' }} className={"shadow"}>
                                <CardContent style={cardStyles}>
                                    <div className="row">
                                        <div className="col-4 d-flex align-items-center">
                                            <CalendarToday style={{ ...iconStyles, color: '#fff' }} />
                                        </div>
                                        <div className="col-8 text-center">
                                            <Typography variant="h6" gutterBottom>
                                                Visits This Month
                                            </Typography>
                                            <Typography variant="h3" style={{ fontWeight: 700 }}>
                                                {dashboardData.monthlyVisits}
                                            </Typography>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Total Patients Card */}
                        <Grid item xs={12} md={4}>
                            <Card style={{ backgroundColor: '#f89848' }} className={"shadow"}>
                                <CardContent style={cardStyles}>
                                    <div className="row">
                                        <div className="col-4 d-flex align-items-center">
                                            <People style={{ ...iconStyles, color: '#fff' }} />
                                        </div>
                                        <div className="col-8 text-center">
                                            <Typography variant="h6" gutterBottom>
                                                Total Patients
                                            </Typography>
                                            <Typography variant="h3" style={{ fontWeight: 700 }}>
                                                {dashboardData.totalPatients}
                                            </Typography>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Recent Visits Table */}
                    <div className="mt-5">
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                            <Typography variant="h6" gutterBottom>Recent Patient Visits</Typography>
                            <div className="d-flex align-items-center gap-3">
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel id="per-page-label">Per Page</InputLabel>
                                    <Select
                                        labelId="per-page-label"
                                        value={visitsPerPage}
                                        label="Per Page"
                                        onChange={(e) => { setVisitsPage(1); setVisitsPerPage(Number(e.target.value)); }}
                                    >
                                        {[5,10,20,50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography variant="body2" color="textSecondary">
                                    Total: {visitsTotal}
                                </Typography>
                            </div>
                        </div>
                        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 1 }}>
                            <Table sx={{ minWidth: 650 }} aria-label="recent visits table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#449998' }}>
                                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Patient Name</TableCell>
                                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Visit Date</TableCell>
                                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Diagnose</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visitsLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">Loading...</TableCell>
                                        </TableRow>
                                    ) : recentVisits.length > 0 ? recentVisits.map((visit) => (
                                        <TableRow
                                            key={visit.id}
                                            sx={{
                                                '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                                                '&:hover': { backgroundColor: 'rgba(68,153,152,0.09)', cursor: 'pointer' },
                                            }}
                                        >
                                            <TableCell component="th" scope="row">{visit.patient_name}</TableCell>
                                            <TableCell>{formatDate(visit.visit_date)}</TableCell>
                                            <TableCell>{visit.diagnose || 'N/A'}</TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography variant="body1" color="textSecondary">No visits found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Stack direction="row" justifyContent="center" mt={3}>
                            <Pagination
                                count={visitsTotalPages}
                                page={visitsPage}
                                color="primary"
                                onChange={(_, val) => setVisitsPage(val)}
                                showFirstButton
                                showLastButton
                            />
                        </Stack>
                    </div>

                    {error && <p className="text-danger mt-3">{error}</p>}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
